import type { IssueSeverity } from '@/shared/constants/issue-status'

import { attention, ATTENTION_BANDS } from './attention'
import { FUNNEL_STEPS } from './coverage'
import { SITE_STATUS_META } from './lens'

import type { GisIssue, MandalAggregate } from '../api/types'

/** One slice of a composition pie. Rendered in array order, clockwise from 12 o'clock. */
export interface PieSegment {
  key: string
  label: string
  color: string
  count: number
}

/**
 * Normalize a chart's `active` prop (a single key, a multi-select array, or '') to a Set of the keys
 * to spotlight — or `null` when nothing is active (all slices full opacity). Shared by `CompositionDonut`
 * and `CompositionPie` so single-select and multi-select callers dim arcs identically.
 */
export function activeKeySet(active: string | readonly string[] | undefined): Set<string> | null {
  const keys = typeof active === 'string' ? (active === '' ? [] : [active]) : (active ?? [])
  return keys.length === 0 ? null : new Set(keys)
}

const SEVERITY_META: { key: IssueSeverity; label: string; color: string }[] = [
  { key: 'critical', label: 'Critical', color: '#dc2626' },
  { key: 'non_critical', label: 'Non-critical', color: '#ea580c' },
]

/** Issues pie — severity composition (matches the existing donut palette). */
export function severitySegments(counts: Record<IssueSeverity, number>): PieSegment[] {
  return SEVERITY_META.map((s) => ({ key: s.key, label: s.label, color: s.color, count: counts[s.key] }))
}

// Qualitative palette for the issue-type donut (distinct hues, in assignment order). "Other" and the
// null "Uncategorised" bucket both take the neutral grey.
const CATEGORY_PALETTE = ['#2f6fed', '#7c4dcf', '#0f9d8c', '#e0812b', '#d24d6b', '#3b8f4a']
const CATEGORY_NEUTRAL = '#94a3b8'

/**
 * Issues-by-type composition — top-`topN` categories by count, the rest folded into one "Other"
 * slice; a null category becomes an "Uncategorised" slice. Keys prefixed `__` (`__other__`,
 * `__none__`) carry no real facet value, so callers must not drill them.
 */
export function categorySegments(issues: GisIssue[], topN = 5): PieSegment[] {
  const counts = new Map<string | null, number>()
  for (const i of issues) {
    counts.set(i.category, (counts.get(i.category) ?? 0) + 1)
  }
  const sorted = [...counts.entries()].sort(
    (a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])),
  )
  const segments: PieSegment[] = []
  let otherCount = 0
  sorted.forEach(([value, count], idx) => {
    if (idx >= topN) {
      otherCount += count
      return
    }
    segments.push({
      key: value ?? '__none__',
      label: value ?? 'Uncategorised',
      color: value === null ? CATEGORY_NEUTRAL : (CATEGORY_PALETTE[idx] ?? CATEGORY_NEUTRAL),
      count,
    })
  })
  if (otherCount > 0) {
    segments.push({ key: '__other__', label: 'Other', color: CATEGORY_NEUTRAL, count: otherCount })
  }
  return segments
}

/**
 * Overview pie — mandals bucketed by Attention Score into four fixed bands. Colours are sourced from
 * the canonical `ATTENTION_BANDS` (attention.ts) so the pie can never drift from the choropleth; the
 * pie collapses the two lowest non-zero bands (10–19 + 1–9) into one readable "Watch" slice.
 */
export const ATT_BUCKETS = [
  { key: 'critical', label: 'Critical', color: ATTENTION_BANDS[0]!.color, test: (s: number) => s >= 35 },
  { key: 'atrisk', label: 'At-risk', color: ATTENTION_BANDS[1]!.color, test: (s: number) => s >= 20 && s < 35 },
  { key: 'watch', label: 'Watch', color: ATTENTION_BANDS[2]!.color, test: (s: number) => s >= 1 && s < 20 },
  { key: 'ok', label: 'OK', color: ATTENTION_BANDS[4]!.color, test: (s: number) => s === 0 },
] as const

export function attentionBandSegments(mandals: MandalAggregate[]): PieSegment[] {
  const scores = mandals.map((m) => attention(m).score)
  return ATT_BUCKETS.map((b) => ({ key: b.key, label: b.label, color: b.color, count: scores.filter(b.test).length }))
}

/** Coverage pie — sites by survey status, summed across mandals (uses the funnel field/colour map). */
export function coverageSegments(mandals: MandalAggregate[]): PieSegment[] {
  return FUNNEL_STEPS.map((s) => ({
    key: s.key,
    label: s.label, // FUNNEL_STEPS owns the label (matches the adjacent funnel); SITE_STATUS_META owns the colour
    color: SITE_STATUS_META[s.key].color,
    count: mandals.reduce((a, m) => a + m[s.field], 0),
  }))
}

/** Officers pie — officers bucketed by completion band. Null completion (no data) is excluded. */
export const OFF_BUCKETS = [
  { key: 'ontrack', label: 'On-track', color: '#0f7a52', test: (p: number) => p >= 85 },
  { key: 'watch', label: 'Watch', color: '#ed8a0a', test: (p: number) => p >= 60 && p < 85 },
  { key: 'below', label: 'Below 60', color: '#e02d22', test: (p: number) => p < 60 },
] as const

export function officerBandSegments(officers: { completionPct: number | null }[]): PieSegment[] {
  return OFF_BUCKETS.map((b) => ({
    key: b.key,
    label: b.label,
    color: b.color,
    count: officers.filter((o) => o.completionPct !== null && b.test(o.completionPct)).length,
  }))
}

/**
 * The slice→filter predicate for a bucket key — the Overview/Officers tabs feed the clicked pie-slice
 * key here to filter their list by the SAME band test the pie builder uses, so the clicked slice and
 * the filtered rows can never drift. Returns `undefined` for an empty/unknown key (= "no filter").
 */
export function bandPredicate(
  buckets: readonly { key: string; test: (n: number) => boolean }[],
  key: string,
): ((n: number) => boolean) | undefined {
  return buckets.find((b) => b.key === key)?.test
}
